import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { generatePath, useNavigate } from 'react-router-dom'
import { Button, SearchInput, InlineAlert } from '../components/BaseComponents'
import { SortConfig } from '../components/BaseComponents/Datagrid/useSortableData'
import PageHeadings from '../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../components/BaseComponents/PageTemplate/PageTemplate'
import Pagination from '../components/BaseComponents/Pagination/Pagination'
import SimpleSortTable, {
	TableColumn,
} from '../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import routes from '../components/Router/routes'
import { useDebouncedSearch } from '../hooks/useDebouncedSearch'
import { getSuppliers, GET_SUPPLIERS_LIMIT } from '../redux/actions/suppliersActions'
import { useAppDispatch, useAppSelector } from '../redux/hooks/reduxHooks'
import {
	selectAllSuppliers,
	selectSuppliersPagination,
	selectSuppliersStatus,
	SUPPLIERS_RESET,
} from '../redux/slices/suppliersSlice'
import { FetchStatus } from '../types/LoadingStates'
import { Supplier } from '../types/Supplier'
import useApi from '../hooks/useApi'

const CompanySuppliers = () => {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()
	const api = useApi();

	const suppliersStatus = useAppSelector(selectSuppliersStatus)
	const pagination = useAppSelector(selectSuppliersPagination)
	const allSuppliers = useAppSelector(selectAllSuppliers)

	const [searchKeyword, setSearchKeyword] = useState('')

	const [orderBy, setOrderBy] = useState<string>('name')
	const [direction, setDirection] = useState<'asc' | 'desc' | undefined>('asc')

	// Data loading
	useEffect(() => {
		dispatch({
			type: SUPPLIERS_RESET,
		})
		dispatch(getSuppliers(api))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{ id: 'name', label: 'Supplier', sortable: true },
		{
			id: 'isicCode',
			label: 'Sector Code (ISIC)',
			sortable: false,
			columnClass: 'col-span-2',
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>
							{rowData.isicCode}
							{rowData.isicCodeName && (
								<span className='text-charcoal-050'> - {rowData.isicCodeName}</span>
							)}
						</p>
					</>
				)
			},
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			sortable: false,
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>{rowData.jurisdiction_code}</p>
					</>
				)
			},
		},
		{
			id: 'edit',
			label: '',
			sortable: false,
			renderer: (columnKey, rowData) => {
				return (
					<Button
						requiresWrite
						type='Primary'
						size='sm'
						text='Edit supplier'
						hasLeadingIcon
						icon='fa-solid fa-pen-to-square'
						accessibilityLabel={rowData.itemName}
						additionalClass='float-right'
						onClick={() => {
							navigate(generatePath(routes.companySupplierEdit.path, { supplierId: rowData._id}))
						}}
					/>
				)
			},
		},
	]
	const defaultSorting: SortConfig = {
		key: 'name',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructData = (dataSet: Array<Supplier>) => [
		...dataSet.map((supplier) => {
			return {
				_id: supplier.id,
				name: supplier.name,
				isicCode: supplier.supplierIndustryCode?.code || '-',
				isicCodeName: supplier.supplierIndustryCode?.name || undefined,
				jurisdiction_code: supplier.jurisdiction_code || '-',
				edit: '',
			}
		}),
	]

	// Timeout for search
	const runSearch = () => {
		dispatch(
			getSuppliers(api, {
				page: pagination.first_page,
				limit: GET_SUPPLIERS_LIMIT,
				orderBy: orderBy,
				direction: 'asc',
				q: searchKeyword,
			})
		)
	}
	const { searchInProgress, flush: flushSearch } = useDebouncedSearch(searchKeyword, runSearch)

	return (
		<PageTemplate>
			<Helmet>
				<title>{routes.companySuppliers.name}</title>
			</Helmet>
			<PageHeadings crumbs={[{ label: 'Purchases', route: routes.companyPurchases.path }, { label: routes.companySuppliers.menuName as string }]} />

			{suppliersStatus === FetchStatus.error && (
				<InlineAlert type='error' className='mt-4'>					<p className='text-white'>
						There was an error fetching suppliers associated with this organisation. If this problem
						persists, please contact the Neoni team.
					</p>
				</InlineAlert>
			)}

			<>
				<div className='items-start'>
					<SearchInput
						name='filterSuppliers'
						placeholder='Search for a supplier'
						value={searchKeyword}
						onChange={(event) => {
							setSearchKeyword(event.target.value)
						}}
						onKeyDown={(event) => {
							if (event.key === 'Enter') {
								flushSearch()
							}
						}}
						onClear={() => {
							setSearchKeyword('')
							dispatch(
								getSuppliers(api, {
									page: pagination.first_page,
									limit: GET_SUPPLIERS_LIMIT,
									orderBy: orderBy,
									direction: direction,
									q: '',
								})
							)
						}}
						isLoading={searchInProgress}
						className='max-w-2xl'
					/>
				</div>

				<SimpleSortTable
					isLoading={suppliersStatus === FetchStatus.loading}
					additionalClass='mt-4'
					data={constructData(allSuppliers)}
					columns={columns}
					defaultSorting={defaultSorting}
					onSortPress={(key, direction) => {
						// Store current sorting values for pagination to reference in its API calls
						setOrderBy(key)
						setDirection(direction === 'ascending' ? 'asc' : 'desc')
						dispatch(
							getSuppliers(api, {
								page: pagination.current_page,
								limit: GET_SUPPLIERS_LIMIT,
								orderBy: key,
								direction: direction === 'ascending' ? 'asc' : 'desc',
								q: searchKeyword,
							})
						)
					}}
				/>

				<Pagination
					isLoading={suppliersStatus === FetchStatus.loading}
					pagination={pagination}
					onPageChange={(newPageNumber) => {
						dispatch(
							getSuppliers(api, {
								page: newPageNumber,
								limit: GET_SUPPLIERS_LIMIT,
								orderBy: orderBy,
								direction: direction,
								q: searchKeyword,
							})
						)
					}}
				/>
			</>
		</PageTemplate>
	)
}

export default CompanySuppliers
